import React, {useState} from "react";
import {useNavigate} from "react-router-dom";
import {Avatar, Divider, ListItemButton, ListItemIcon, ListItemText} from "@mui/material";
import {
  ChevronRight,
  HelpCenter,
  NetworkCheck,
  Palette,
  Person,
  PowerSettingsNew,
  Psychology,
  RemoveFromQueue,
} from "@mui/icons-material";
import {useUser} from "../user/Context/UserProvider";
import {useSidebarLayout} from "./Context/SidebarProvider";
import {useAuth} from "../authentication/Context/AuthProvider";
import {useTheme} from "../../context/ThemeWrapperProvider";
import {useOrgSettings} from "../../context/OrgSettingsProvider";
import {statusPage} from "../../resources/environment";
import {SidebarOverlay} from "./SidebarOverlay";
import StyledTooltip from "../tooltips/StyledTooltip";
import {useSidebarItemStyles, useSidebarIconStyles} from "./sidebarStyles";
import useSidebarOverlay from "./useSidebarOverlay";
import Powerbar, {usePowerbar} from "../powerbar/Powerbar";
import {usePermissions} from "../../context/AuthzProvider";
import UserMemoryDialog from "../../pages/UserProfile/Dialogs/UserMemoryDialog";
import {map} from "underscore";

const SidebarProfile = () => {
  const {user} = useUser();
  const {isSidebarExpanded, isSidebarTextVisible, setCurrentPage} = useSidebarLayout();
  const {activeTheme, toggleActiveTheme} = useTheme();
  const {aiPermissions} = useOrgSettings();
  const {canAccessTrustedAdvisor, canAccessHelpGuides} = usePermissions();
  const auth = useAuth();
  const navigate = useNavigate();
  const {ref, isOpen, toggle, close} = useSidebarOverlay();
  const powerbar = usePowerbar();
  const currentThemeName = activeTheme?.palette?.themeName;

  const [themeMenuAnchor, setThemeMenuAnchor] = useState(null);
  const [memoryDialogOpen, setMemoryDialogOpen] = useState(false);

  const displayName = user?.nickname || user?.fullName || user?.email || "";
  const {classes} = useSidebarItemStyles({
    isTextHidden: !isSidebarTextVisible,
    inOverlay: false,
  });
  const {classes: iconClasses} = useSidebarIconStyles();

  const handleMenuAction = (action) => {
    close();
    action();
  };

  const handleThemeMenuOpen = (event) => {
    event.stopPropagation();
    setThemeMenuAnchor(event.currentTarget);
  };

  const handleThemeMenuClose = () => {
    setThemeMenuAnchor(null);
  };

  const handleThemeSelect = (themeName) => {
    toggleActiveTheme(themeName);
    handleThemeMenuClose();
    close();
  };

  const avatar = (
    <Avatar src={user?.profileDocumentPreview} sx={{width: 28, height: 28, fontSize: "0.875rem"}}>
      {displayName.charAt(0)}
    </Avatar>
  );

  const profileButton = (
    <ListItemButton
      ref={ref}
      onClick={toggle}
      className={classes.item}
      id="sidebar-profile"
      aria-label={!isSidebarExpanded ? "Profile" : undefined}
      aria-haspopup="true"
      aria-expanded={isOpen}
    >
      <ListItemIcon className={iconClasses.listItemIcon}>{avatar}</ListItemIcon>
      <ListItemText
        primary={displayName}
        className={isSidebarTextVisible ? classes.overlayItemText : classes.itemText}
        primaryTypographyProps={{variant: "body2", noWrap: true}}
      />
      <ChevronRight fontSize="small" className={classes.chevronRight} />
    </ListItemButton>
  );

  const themeOptions = [
    {text: "BreachRx", value: "breachrx"},
    {text: "Light", value: "light"},
    {text: "Dark", value: "dark"},
  ];

  const menuItems = [
    {
      text: "Manage Profile",
      icon: <Person fontSize="small" />,
      id: "sidebar-manage-profile",
      onClick: () =>
        handleMenuAction(() => {
          navigate("/userProfile");
          setCurrentPage("/userProfile");
        }),
    },
    {
      text: "Themes",
      icon: <Palette fontSize="small" />,
      id: "sidebar-themes",
      hasSubmenu: true,
      onClick: handleThemeMenuOpen,
    },
    ...(canAccessHelpGuides ?
      [
        {
          text: "Help Guides",
          icon: <HelpCenter fontSize="small" />,
          id: "sidebar-help-guides",
          onClick: () =>
            handleMenuAction(() => {
              navigate("/helpGuides");
              setCurrentPage("/helpGuides");
            }),
        },
      ] :
      []),
    {
      text: "BreachRx Status",
      icon: <NetworkCheck fontSize="small" />,
      id: "sidebar-status",
      onClick: () => handleMenuAction(() => window.open(statusPage)),
    },
    {
      text: "Powerbar",
      icon: <RemoveFromQueue fontSize="small" />,
      id: "sidebar-powerbar",
      onClick: () => handleMenuAction(powerbar.setOpen),
    },
    ...(canAccessTrustedAdvisor && aiPermissions?.userMemoryOrgEnabled ?
      [
        {
          text: "Manage My Memory",
          icon: <Psychology fontSize="small" />,
          id: "sidebar-manage-memory",
          onClick: () => handleMenuAction(() => setMemoryDialogOpen(true)),
        },
      ] :
      []),
    {
      text: "Logout",
      icon: <PowerSettingsNew fontSize="small" />,
      id: "sidebar-logout",
      onClick: () => handleMenuAction(() => auth.logout()),
    },
  ];

  return (
    <>
      <div>
        {isSidebarExpanded ?
          (
            profileButton
          ) :
          (
            <StyledTooltip title="Profile" placement="right" opacity={isOpen ? 0 : 1}>
              {profileButton}
            </StyledTooltip>
          )}
        <SidebarOverlay
          open={isOpen}
          anchorEl={ref.current}
          onClose={close}
          parentText={!isSidebarExpanded ? displayName : null}
        >
          {map(menuItems, (item, index) => [
            index > 0 && <Divider key={`divider-${item.id}`} />,
            <ListItemButton
              key={item.id}
              onClick={item.onClick}
              id={item.id}
              sx={item.hasSubmenu ? {justifyContent: "space-between"} : undefined}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} primaryTypographyProps={{variant: "body2"}} />
              {item.hasSubmenu && <ChevronRight fontSize="small" />}
            </ListItemButton>,
            item.hasSubmenu && (
              <SidebarOverlay
                key={`${item.id}-submenu`}
                open={!!themeMenuAnchor}
                anchorEl={themeMenuAnchor}
                onClose={handleThemeMenuClose}
              >
                {map(themeOptions, (option) => (
                  <ListItemButton
                    key={option.value}
                    onClick={() => handleThemeSelect(option.value)}
                    id={`sidebar-theme-${option.value}`}
                    className={classes.item}
                    sx={{
                      paddingLeft: 2,
                      background:
                        currentThemeName === option.value ? "rgba(0, 0, 0, 0.08)" : undefined,
                    }}
                  >
                    <ListItemText
                      primary={option.text}
                      primaryTypographyProps={{variant: "body2"}}
                    />
                  </ListItemButton>
                ))}
              </SidebarOverlay>
            ),
          ])}
        </SidebarOverlay>
      </div>
      <Powerbar state={powerbar} />
      {memoryDialogOpen && (
        <UserMemoryDialog isOpen={memoryDialogOpen} onClose={() => setMemoryDialogOpen(false)} />
      )}
    </>
  );
};

export default SidebarProfile;
